import { IonContent, IonFooter } from '@ionic/react';
import { CompleteBasketItem } from '../../../../providers/BasketProvider';
import useAuth from '../../../../hooks/global/useAuth';
import useIsCheckoutDisabled from '../../../../hooks/basket/useIsCheckoutDisabled';
import PageHeader from '../../components/headers/PageHeader';
import useRouter from '../../../../hooks/navigation/useRouter';
import { Modals, Routes } from '../../exports/Enums';
import useModalRouter from '../../common/hooks/useModalRouter';
import useRestaurantClosedFallback from '../../common/hooks/useRestaurantClosedFallback';
import { useTranslation } from 'react-i18next';
import Button from '../../components/buttons/Button';
import CheckoutButton from '../../../../components-library/buttons/CheckoutButton';
import OrderValidationManager from '../../../../services/api/OrderValidationManager';
import useOrderFlow from '../../../../hooks/order/useOrderFlow';
import useDeliveryOrderMethodValidation from '../../modals/order-method/useDeliveryOrderMethodValidation';
import { ReactElement, useRef, useState } from 'react';
import Alert from '../../components/modals/Alert';
import { RenderableErrorResponse } from '../../../../services/exports/Types';
import useOrderErrorHandler from '../../common/hooks/error-handlers/useOrderErrorHandler';
import { swipeableCssClass } from '../../exports/Constants';
import OrderBasket from '../../../../components-library/order-basket';
import * as Sentry from '@sentry/react';
import { describeCartValidationError } from './orderValidationError';
import { ReservedHttpResponseCodes } from '../../../../services/exports/Types';
import ReduxActions from '../../../../store/ReduxActions';
import { actionCreators } from '../../../../store/actions';
import ProfileManager from '../../../../services/api/ProfileManager';
import toast from 'react-hot-toast';

const CartPage = (): ReactElement => {
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Pages:CartPage' });
  const { t: tErrors } = useTranslation(undefined, { keyPrefix: 'Modules:Mobile:Shared:Errors' });
  const { openModal, closeModal } = useModalRouter();
  const { isLoggedIn } = useAuth();
  const isCheckoutDisabled = useIsCheckoutDisabled();
  const { navigate, goBack } = useRouter();
  const { getOrderData } = useOrderFlow();
  const orderData = getOrderData();
  const { validate } = useRestaurantClosedFallback({
    orderMethod: orderData.method,
    scheduledFor: orderData.scheduled_for,
  });
  const orderErrorHandler = useOrderErrorHandler();
  const { handleValidationError } = useDeliveryOrderMethodValidation();
  const [isMobileAlertOpen, setIsMobileAlertOpen] = useState(false);
  const mobileAppGenericError = useRef(null);
  const toggleMobileAlert = () => setIsMobileAlertOpen((prev) => !prev);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * A points redemption that no longer fits the balance (spent since it was persisted, or the
   * balance dropped below the program minimum) is dropped and the order re-validated without it,
   * instead of dead-ending the customer on an alert they cannot act on.
   */
  const validateOrder = async () => {
    const result = await OrderValidationManager.validate(orderData);

    if (result.success || result.status !== ReservedHttpResponseCodes.ValidationError) {
      return result;
    }

    if (!describeCartValidationError(result.response?.response?.data).rejectsOnlyCashbackPoints) {
      return result;
    }

    const withoutPoints = { coupon_type: null, coupon_details: null, cashback_points: null };

    ReduxActions.dispatch(actionCreators.order.updateOrder(withoutPoints));
    orderData.company_id && ProfileManager.getCashbackAccount(orderData.company_id);
    toast(t('toasts.cashback_points_removed'));

    return OrderValidationManager.validate(getOrderData(withoutPoints));
  };

  const onCheckoutClick = async (): Promise<void> => {
    setIsLoading(true);

    const isBusinessTimeValid = validate();

    if (!isBusinessTimeValid) {
      setIsLoading(false);

      return;
    }

    const { success, response, status } = await validateOrder();

    if (success) {
      setIsLoading(false);

      return navigate({
        mobile: {
          path: Routes.Checkout,
        },
      });
    }

    setIsLoading(false);

    if (status === ReservedHttpResponseCodes.ValidationError) {
      const validation = describeCartValidationError(response.response?.data);

      // Field names only, never values: enough to tell which rule a customer tripped.
      if (validation.opensOrderMethod) {
        // Expected path: the order-method modal collects what is missing. A log, not an
        // issue, so it stays queryable without paging the alert channel.
        Sentry.logger.info('[cart] order validation sent to order-method modal', {
          fields: validation.fields.join(','),
        });

        return openModal({
          id: Modals.OrderMethod,
        });
      }

      if (validation.fixableInCheckout) {
        Sentry.logger.info('[cart] order validation sent to checkout form', {
          fields: validation.fields.join(','),
        });

        return navigate({
          mobile: {
            path: Routes.Checkout,
          },
        });
      }

      Sentry.captureMessage('[cart] order validation rejected', {
        level: 'warning',
        tags: { flow: 'cart_order_validation' },
        fingerprint: ['cart_order_validation', ...validation.fields],
        contexts: { validation: { fields: validation.fields } },
      });

      mobileAppGenericError.current = validation.message ?? tErrors('internal_error');

      return toggleMobileAlert();
    }

    const errorResponse = response as any as RenderableErrorResponse;
    const validationError = handleValidationError(errorResponse, tErrors('internal_error'));

    if (validationError) {
      return openModal({
        id: Modals.OrderMethod,
        props: {
          deliveryAddressError: validationError.deliveryAddress,
          scheduledForError: validationError.scheduledFor,
        },
      });
    }

    orderErrorHandler(errorResponse);

    mobileAppGenericError.current =
      errorResponse.response?.data?.description || errorResponse.response?.data?.title || tErrors('internal_error');
    toggleMobileAlert();
  };

  const onBasketItemUpdateClick = (index: number, basketItem: CompleteBasketItem): void => {
    openModal({
      id: Modals.MenuItem,
      presentation: {
        initialBreakpoint: 1,
        breakpoints: [0, 1],
        handle: false,
        cssClass: swipeableCssClass,
      },
      props: {
        product: basketItem.entry,
        lineItem: basketItem,
        onCloseModal: () => closeModal(Modals.MenuItem),
        basketItemIndex: index,
      },
    });
  };

  return (
    <>
      <Alert
        title={mobileAppGenericError.current}
        isOpen={isMobileAlertOpen}
        onDidDismiss={toggleMobileAlert}
        buttons={[
          {
            text: t('go_to_menu'),
            handler: goBack,
          },
        ]}
      />
      <PageHeader title={t('title')} />
      <IonContent>
        <OrderBasket
          onBasketItemUpdateClick={onBasketItemUpdateClick}
          onCheckoutClick={onCheckoutClick}
          loading={isLoading}
          checkoutDisabled={!isLoggedIn}
        />
      </IonContent>
      <IonFooter>
        <div className="!p-0 [&::part(container)]:p-0 safe-area-bottom">
          {isLoggedIn ? (
            <CheckoutButton
              loading={isLoading}
              title={t('buttons.checkout')}
              onClick={onCheckoutClick}
              hideAddProductsButton
              disabled={isCheckoutDisabled}
            />
          ) : (
            <CheckoutButton
              title={t('buttons.checkout')}
              onClick={onCheckoutClick}
              hideAddProductsButton
              disabled={isCheckoutDisabled}
            >
              <Button title={t('buttons.login_to_checkout')} onClick={() => openModal({ id: Modals.Login })} />
            </CheckoutButton>
          )}
        </div>
      </IonFooter>
    </>
  );
};

export default CartPage;
